import parsePhoneNumberFromString, {
  type CountryCode,
  formatIncompletePhoneNumber,
  getCountryCallingCode,
  isSupportedCountry,
} from 'libphonenumber-js/mobile'
import { type Ref } from 'vue'

import { getAllCountries } from '@tix/common-utils'

export type CountryOption = {
  countryCode: string
  label: string
  callingCode: string
}
export const getAllCountryOptions = (): CountryOption[] =>
  getAllCountries()
    .filter(({ iso2 }) => isSupportedCountry(iso2))
    .sort((a, b) => a.country.localeCompare(b.country))
    .map(({ country, iso2 }) => {
      return {
        countryCode: iso2,
        label: country,
        callingCode: `+${getCountryCallingCode(iso2 as CountryCode)}`,
      }
    })

export const parsePhoneNumber = (
  number: string,
  countryCodeRef: Ref<CountryCode>,
  defaultCountry: CountryCode,
): string => {
  const parsedPhoneNumber = parsePhoneNumberFromString(number, countryCodeRef.value)

  if (!parsedPhoneNumber) return number

  // libphonenumber-js can usually tell apart countries that share the same calling code
  // update selected country based on the value determined from the entered number
  if (parsedPhoneNumber.country && parsedPhoneNumber.country !== countryCodeRef.value) {
    countryCodeRef.value = parsedPhoneNumber.country
  }

  // Make a guess based on possible countries
  if (!parsedPhoneNumber.country) {
    const possibleCountries = parsedPhoneNumber.getPossibleCountries()
    if (possibleCountries.length < 1) {
      return parsedPhoneNumber.number
    }

    // Prefer default country if possible option
    /* v8 ignore next 3 -  triggering this branch requires parser to return no .country but include default in getPossibleCountries(); libphonenumber does not do that consistently for our test inputs. -- @preserve */
    if (possibleCountries.includes(defaultCountry)) {
      countryCodeRef.value = defaultCountry
      return parsedPhoneNumber.number
    }

    // Otherwise pick first possible country as best guess
    countryCodeRef.value = possibleCountries[0]
  }

  return parsedPhoneNumber.number
}

export const formatPhoneNumber = (value: string, selectedCountry: CountryCode): string => {
  if (!value) return ''

  const parsedPhoneNumber = parsePhoneNumberFromString(value, selectedCountry)

  if (!parsedPhoneNumber) return value

  // libphonenumber-js can usually tell apart countries that share the same calling code
  // If country isn't yet determined, do not format away the calling code
  if (!parsedPhoneNumber.country) return formatIncompletePhoneNumber(value)

  if (!parsedPhoneNumber.isValid()) return formatIncompletePhoneNumber(value, parsedPhoneNumber.country)

  // Only use formatNational() on valid numbers to avoid bad UX (e.g. 021 188 -> 21188)
  return formatIncompletePhoneNumber(parsedPhoneNumber.formatNational(), parsedPhoneNumber.country)
}
